import i18n from '@yandex-int/i18n';
import axios, { AxiosResponse } from 'axios';
import { translateAppStoreError } from 'client/utils/errorMessage';
import * as skillPublishingKeyset from 'i18n/SkillPublishing';
import JSONPointer from 'jsonpointer';

import { Operation, OperationType } from '../model/operation';

import { ApiError, ApiErrorType } from './error';

const tSkillPublishing = i18n(skillPublishingKeyset);

interface CallParams {
    method: string;
    url: string;
    data?: FormData | object;
    onUploadProgress?: (progressEvent: unknown) => void;
    queryParams?: Record<string, unknown>;
    timeout?: number;
}

interface IValidationError {
    key?: string;
    type: string;
    arg?: Record<string, string | number>;
}

function callWithoutWrap<T>({ data, method, url, onUploadProgress, timeout, queryParams }: CallParams) {
    const headers = {
        'x-csrf-token': __appConfig.secretkey,
        ...(!(data instanceof FormData) && {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        }),
    };
    const body = !data || data instanceof FormData ? data : JSON.stringify(data);

    return axios<CallParams, AxiosResponse<T>>(url, {
        data: body,
        withCredentials: true,
        headers,
        method,
        onUploadProgress,
        params: queryParams,
        validateStatus: () => true,
        timeout,
    });
}

export function callPublicApi<T = void>(params: CallParams): Promise<T> {
    return callWithoutWrap<T>(params).then(async (res) => {
        const body = res.data;

        if (res.status >= 200 && res.status < 300) {
            return body;
        }

        throw body;
    });
}

const getTypedErrors = <T>(data: T) => {
    if (!!data && typeof data === 'object' && 'validationErrors' in data && Array.isArray(data.validationErrors)) {
        return data.validationErrors;
    }
    return;
};

export function callApi<T = void>(params: CallParams): Promise<T> {
    return callWithoutWrap<T>(params)
        .then((response) => {
            if (response.status === 429) {
                throw new ApiError(response.statusText, { code: response.status });
            }

            return response;
        })
        .then((response) => {
            const data = response.data;

            if (isAppStoreError(response)) {
                const errors = response.data.validationErrors;
                const fields = {};
                for (const error of errors) {
                    JSONPointer.set(fields, `/${error.key || 'common'}`, translateAppStoreError(error.type, error.arg));
                }
                throw new ApiError(translateAppStoreError(errors[0]?.type, errors[0]?.arg), {
                    code: response.status,
                    fields,
                    payload: { typedErrors: getTypedErrors(data) },
                });
            }

            if (isError(data)) {
                const { message, code, fields } = data.error;
                const { payload } = data;

                throw new ApiError(message, { code, fields, payload });
            }

            if (
                response.status >= 400 &&
                data &&
                typeof data === 'object' &&
                'message' in data &&
                typeof data.message === 'string'
            ) {
                throw new ApiError(data.message, {
                    code: response.status,
                    payload: { typedErrors: getTypedErrors(data) },
                });
            }

            if (response.status >= 400 && typeof data === 'string') {
                throw new ApiError(data, { code: response.status });
            }

            if (response.status >= 400) {
                throw new ApiError('Unknown error', { code: response.status });
            }

            if (isResult(data)) {
                return data.result as T;
            }

            if (response.status === 200) {
                return data as T;
            }

            throw new Error('Malformed API response');
        });
}

interface ResultWrapper<Result> {
    result: Result;
}

interface ErrorWrapper {
    error: ApiError;
    payload: ApiErrorType['payload'];
}

const isError = (res: unknown): res is ErrorWrapper => {
    return Boolean(res && typeof res === 'object' && 'error' in res && res.error);
};

const isAppStoreError = <T extends AxiosResponse>(
    response: T,
): response is T & { data: { validationErrors: IValidationError[] } } => {
    return (
        response.status >= 400 &&
        'data' in response &&
        typeof response.data === 'object' &&
        !!response.data &&
        'validationErrors' in response.data &&
        Array.isArray(response.data.validationErrors) &&
        response.data.validationErrors.every((error: IValidationError) => !!error.type)
    );
};

const isResult = <Result>(res: unknown): res is ResultWrapper<Result> => {
    return Boolean(res && typeof res === 'object' && 'result' in res && res.result);
};

export const translateOperations = (operations: Array<Operation>) => {
    return operations.map((operation) => {
        let comment = operation.comment;
        if (operation.comment === skillPublishingKeyset.ru['unknown-error']) {
            comment = tSkillPublishing('unknown-error');
        }
        if (
            operation.comment === skillPublishingKeyset.en['publication-canceled-by-user'] &&
            operation.type === OperationType.ReviewCancelled
        ) {
            comment = tSkillPublishing('publication-canceled-by-user');
        }
        return {
            ...operation,
            comment,
        };
    });
};
